import React, { useMemo, useState } from 'react';
import CertificateItem from './CertificateItem';
import { CERTIFICATES, certificates, CLOUD_CERTIFICATES } from './certificates';
import { CertificateService } from './certificateService';
import { useLoading } from '../../helpers/useLoading';
import { toast } from '../../helpers/toast';
import { Button } from '../../components/UI-CHAKRA/Button';
import { Text } from '../../components/UI-CHAKRA/Text';
import { Spinner } from '../../components/UI-CHAKRA/Spinner';
import { FormLabel } from '../../components/UI-CHAKRA/FormLabel';
import { Input } from '../../components/UI-CHAKRA/Input/Input';
import { FormControl } from '../../components/UI-CHAKRA/FormControl';
import BaseDialog from '../../components/Dialogs/BaseDialog';
import { Flex } from '../../components/UI-CHAKRA/Flex';

interface AuthenticateCloudCertificateDialogWithPinAndOTPProps {
  onCancel(): void;
  onAuthenticated(): void;
  open: boolean;
  prescriptionId: number;
  provider: CLOUD_CERTIFICATES;
  needsOTP: boolean;
}

export function AuthenticateCloudCertificateDialogWithPinAndOTP(
  props: AuthenticateCloudCertificateDialogWithPinAndOTPProps
) {
  const [open, setOpen] = useState(props.open);
  const [pin, setPin] = useState<string>('');
  const [otp, setOtp] = useState<string>('');
  const [loading, runWithLoading] = useLoading();
  const [incorrectPin, setIncorrectPin] = useState(false);
  const [incorrectOtp, setIncorrectOtp] = useState(false);

  const onConfirm = async () => {
    if (loading) return;
    runWithLoading(async () => {
      if (pin.trim() === '') {
        setIncorrectPin(true);
        return;
      }
      if (props.needsOTP && otp.trim() === '') {
        setIncorrectOtp(true);
        return;
      }

      try {
        switch (props.provider) {
          case CERTIFICATES.CERTISIGN: {
            const payload = props.needsOTP
              ? {
                  pin,
                  otp,
                }
              : { pin };

            await CertificateService.certiSign.authenticate(props.prescriptionId, payload);
            break;
          }
        }

        if (incorrectPin) {
          setIncorrectPin(false);
        }
        if (incorrectOtp) {
          setIncorrectOtp(false);
        }

        setOpen(false);
        props.onAuthenticated();
      } catch (e) {
        setIncorrectPin(true);
        setIncorrectOtp(true);
        setPin('');
        setOtp('');

        toast.error(props.needsOTP ? 'PIN e/ou e-Token está(ão) inválido' : 'PIN inválido');
      }
    });
  };

  const onCancel = () => {
    props.onCancel();
    setOpen(false);
  };

  const onChangePin = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPin(event.target.value);
  };

  const onChangeOTP = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(event.target.value);
  };

  const onSubmitPassword = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onConfirm();
  };

  const certificateItem = useMemo(() => {
    const certificate = certificates.find((certificate) => certificate.value === props.provider);
    return certificate ? <CertificateItem certificate={certificate} active={false} /> : null;
  }, [props.provider]);

  const certificateProps = useMemo(() => {
    switch (props.provider) {
      case CERTIFICATES.CERTISIGN: {
        return {
          pinType: 'password',
          otpType: 'text',
          inputLength: 255,
          pinInputLabel: 'PIN',
          tokenInputLabel: 'e-Token',
          dialogTitle: !props.needsOTP
            ? 'Digite o PIN do seu certificado'
            : 'Digite o seu PIN e e-Token do seu certificado',
          dialogSubtitle: '',
        };
      }
    }
  }, [props.provider, props.needsOTP]);

  return (
    <BaseDialog isOpen={open} onClose={() => {}}>
      <Text fontWeight={700} fontSize={'18px'} style={{ marginBottom: '8px' }}>
        {certificateProps && certificateProps.dialogTitle}
      </Text>
      <Text fontSize={'14px'}>{certificateProps && certificateProps.dialogSubtitle}</Text>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ width: 101, marginRight: 16 }}>{certificateItem}</div>
        <form onSubmit={onSubmitPassword} style={{ display: 'flex', flexDirection: 'column' }}>
          <FormControl isInvalid={incorrectPin}>
            <FormLabel>{certificateProps && certificateProps.pinInputLabel}</FormLabel>
            <Input
              w={'100%'}
              value={pin}
              onChange={onChangePin}
              maxLength={certificateProps && certificateProps.inputLength}
              type={certificateProps && certificateProps.pinType}
              autoFocus
              data-testid={'pin'}
            />
          </FormControl>

          {props.needsOTP && (
            <FormControl isInvalid={incorrectOtp} marginTop={'22px'}>
              <FormLabel>{certificateProps && certificateProps.tokenInputLabel}</FormLabel>
              <Input
                w={'100%'}
                value={otp}
                onChange={onChangeOTP}
                maxLength={certificateProps && certificateProps.inputLength}
                type={certificateProps && certificateProps.otpType}
                autoFocus
                data-testid={'token'}
              />
            </FormControl>
          )}
        </form>
      </div>

      <Flex padding={'24px'} gap={'12px'} justifyContent={'flex-end'}>
        <Button
          width={'121px'}
          onClick={onCancel}
          variant={'ghost'}
          bg={'gray.lightest'}
          color={'typography.darkest'}
          boxShadow={'none'}
          fontWeight={400}
          fontSize={'14px'}
        >
          Cancelar
        </Button>
        <Button width={'121px'} onClick={onConfirm} variant={'ghost'} backgroundColor={'info.main'} color={'#fff'}>
          {loading ? <Spinner color={'gray.light'} minH={'18px'} minW={'18px'} /> : 'Confirmar'}
        </Button>
      </Flex>
    </BaseDialog>
  );
}
